import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, DialogueBox } from 'components/UIElements'
import { closeIcon, downloadIcon } from '../../../assets/assets'
import { formatAndSanitizeHTML } from 'utils/misc'
import { CONSENT_SECTION_TYPES, QUESTION_TYPES } from 'utils/constants'
import ConsentPdfViewer from './ConsentPdfViewer'
import {
    SelectOne,
    SelectMultiple,
    TextEntry
} from '../../WebSurveys/components/QuestionTypes/QuestionTypes'
import '../../WebSurveys/stylesheets/WebSurveys.scss'
import SurveyQuestion from '../../WebSurveys/components/SurveyQuestion'

// Supported consent comprehension types map to WebSurvey components
const QUESTION_COMPONENTS = {
    select_one: SelectOne,
    select_multiple: SelectMultiple,
    text: TextEntry
}

const ConsentComprehension = ({
    strings,
    sections,
    onDone,
    // wire submissions into consent redux payload
    completeConsentQuestion,
    webConsentOrderIds = [],
    onBackFromFirst,
    downloadConsentPDF,
    consentName,
    consentVersion,
    consentId,
    isBlockingConsent,
    onConsentClose
}) => {
    // Build order and questions map for the subset to leverage existing logic
    const { order, questions } = useMemo(() => {
        const ids = []
        const map = {}
        sections.forEach(s => {
            map[s.id] = s
            ids.push(s.id)
        })
        return { order: [ids], questions: map }
    }, [sections])

    // Track current id and a local submission object compatible with survey utils
    const [currentId, setCurrentId] = useState(order?.[0]?.[0] || 'submit-id')
    const [submissions, setSubmissions] = useState([])
    const [isSubmitting, setIsSubmitting] = useState(false)

    const isFirst = currentId === order?.[0]?.[0]

    const nextQId = id => {
        const arr = order?.[0] || []
        const idx = arr.indexOf(id)
        if (idx === -1) return 'submit-id'
        const next = arr[idx + 1]
        return next || 'submit-id'
    }

    const handleDone = () => {
        // Prevent multiple submissions
        if (isSubmitting) return
        setIsSubmitting(true)
        onDone()
    }

    const nextQ = id => {
        const next = nextQId(id)
        if (next === 'submit-id') return handleDone()
        setCurrentId(next)
    }

    const renderBackButton = id => {
        const arr = order?.[0] || []
        const idx = arr.indexOf(id)
        const prev = idx > 0 ? arr[idx - 1] : null
        return (
            <Button
                primary
                grey
                icon='fas fa-angle-up'
                content={strings.back}
                disabled={!prev && !onBackFromFirst}
                onClick={e => {
                    e.stopPropagation()
                    if (prev) {
                        setCurrentId(prev)
                    } else if (onBackFromFirst) {
                        onBackFromFirst()
                    }
                }}
            />
        )
    }

    const NextButton = ({ disabled, content, completionFunc }) => (
        <Button
            primary
            className='next'
            disabled={disabled || isSubmitting}
            content={content}
            onClick={() => completionFunc()}
        />
    )

    const setAllRequiredAnswered = () => { }

    // Adapter to capture latest answer objects into local submission list
    const completeQuestion = (submission /*, _order */) => {
        setSubmissions(prev => {
            const existsIdx = prev.findIndex(q => q.id === submission.id)
            if (existsIdx === -1) return [...prev, submission]
            return [
                ...prev.slice(0, existsIdx),
                submission,
                ...prev.slice(existsIdx + 1)
            ]
        })

        // Also mirror into consent submission in Redux so final POST includes answers
        const { id, answer, type } = submission
        const toArray = Array.isArray(answer) ? answer : answer ? [answer] : []
        // For text keep typed value; for select types format value as `${questionId}_${choiceId}` and add question id
        const formatted = type === QUESTION_TYPES.text
            ? toArray
            : toArray.map(ans => {
                const rawValue = ans?.value
                const choiceId = typeof rawValue === 'string' && rawValue.includes('_')
                    ? rawValue.split('_').pop()
                    : rawValue
                return { ...ans, value: `${id}_${choiceId}`, question: id }
            })
        completeConsentQuestion(id, formatted)
    }

    const submissionSurvey = useMemo(() => ({
        questions: submissions
    }), [submissions])

    const current = questions[currentId]

    const renderPrompt = prompt => {
        return (
            <SurveyQuestion id={prompt.id}>
                <div className='make-scrollable-container'>
                    <div className='survey-question prompt'>
                        <p
                            className='question-label'
                            dangerouslySetInnerHTML={{
                                __html: formatAndSanitizeHTML(prompt.label)
                            }}
                        />
                        <div className='button-list'>
                            {renderBackButton(prompt.id)}
                            <Button
                                primary
                                className='next'
                                disabled={isSubmitting}
                                content={strings.next_btn}
                                onClick={() => {
                                    const next = nextQId(prompt.id)
                                    if (next === 'submit-id') {
                                        handleDone()
                                    } else {
                                        setCurrentId(next)
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </SurveyQuestion>
        )
    }

    const renderPdf = pdf => {
        return (
            <SurveyQuestion id={pdf.id}>
                <div className='make-scrollable-container'>
                    <div className='survey-question prompt'>
                        <ConsentPdfViewer
                            url={pdf.url}
                            title={pdf.label || consentName || 'Consent PDF'}
                        />
                        <div className='button-list'>
                            {renderBackButton(pdf.id)}
                            <Button
                                primary
                                className='next'
                                disabled={isSubmitting}
                                content={strings.next_btn}
                                onClick={() => {
                                    const next = nextQId(pdf.id)
                                    if (next === 'submit-id') {
                                        handleDone()
                                    } else {
                                        setCurrentId(next)
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </SurveyQuestion>
        )
    }

    if (!sections?.length) return null

    return (
        <DialogueBox className='dialogue no-humi'>
            {downloadConsentPDF ? (
                <div className='top-section-buttons'>
                    <div
                        className='top-section-icon'
                        onClick={() => {
                            downloadConsentPDF(consentName, consentVersion, consentId)
                        }}
                    >
                        {downloadIcon()}
                    </div>
                    {!isBlockingConsent && typeof onConsentClose === 'function' ? (
                        <div className='top-section-icon' onClick={onConsentClose}>
                            {closeIcon()}
                        </div>
                    ) : null}
                </div>
            ) : null}
            <div className='websurvey-container embedded'>
                {(current?.type === CONSENT_SECTION_TYPES.prompt || current?.type === QUESTION_TYPES.introduction) && renderPrompt(current)}
                {current?.type === CONSENT_SECTION_TYPES.pdf && current?.url && renderPdf(current)}
                {current?.type !== CONSENT_SECTION_TYPES.prompt &&
                    current?.type !== QUESTION_TYPES.introduction &&
                    current?.type !== CONSENT_SECTION_TYPES.pdf &&
                    QUESTION_COMPONENTS[current?.type] ? (
                    React.createElement(QUESTION_COMPONENTS[current.type], {
                        key: current.id,
                        id: current.id,
                        question: current,
                        strings,
                        suppressAutoAdvance: true,
                        NextButton,
                        renderBackButton,
                        setAllRequiredAnswered,
                        // survey utils props
                        webSurvey: { id: 'consent-comprehension', order, questions },
                        submissionSurvey,
                        nextQId,
                        nextQ,
                        completeQuestion,
                        // stored answer support for going back
                        storedAnswer: submissions.find(s => s.id === current.id)?.answer
                    })
                ) : null}
            </div>
        </DialogueBox>
    )
}

ConsentComprehension.propTypes = {
    strings: PropTypes.objectOf(PropTypes.string),
    sections: PropTypes.array,
    onDone: PropTypes.func,
    completeConsentQuestion: PropTypes.func,
    downloadConsentPDF: PropTypes.func,
    consentName: PropTypes.string,
    consentVersion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    consentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isBlockingConsent: PropTypes.bool,
    onConsentClose: PropTypes.func
}

export default ConsentComprehension


